/* eslint-disable react/only-export-components -- lib module: hooks, helpers and route guards live together on purpose */
// Gettext style i18n (FEATURES Scope decision I18N): the English text is the key.
//   t('Write something')            t('{count} members', { count })
//   plural(count, '{count} member', '{count} members')
// Dictionaries live in src/locales/<lang>.json (flat {"English": "Translation"}), lazy loaded.
// English needs no file. Missing keys and missing files fall back to English.
import { createElement, Fragment, useEffect, type ReactNode } from 'react'
import { create } from 'zustand'
import { setFormatLocale, setFormatTranslator } from './format'

export type LanguageCode = 'en' | 'es' | 'de' | 'fr' | 'pt-BR' | 'zh-TW' | 'it' | 'vi' | 'tr' | 'nl' | 'ja'

type LanguageInfo = { code: LanguageCode; name: string; english: string }

/** The 11 languages the product will support, with their native names (picker order). */
export const SUPPORTED_LANGUAGES: ReadonlyArray<LanguageInfo> = [
  { code: 'en', name: 'English', english: 'English' },
  { code: 'es', name: 'Español', english: 'Spanish' },
  { code: 'de', name: 'Deutsch', english: 'German' },
  { code: 'fr', name: 'Français', english: 'French' },
  { code: 'pt-BR', name: 'Português (Brasil)', english: 'Portuguese (Brazil)' },
  { code: 'it', name: 'Italiano', english: 'Italian' },
  { code: 'nl', name: 'Nederlands', english: 'Dutch' },
  { code: 'tr', name: 'Türkçe', english: 'Turkish' },
  { code: 'vi', name: 'Tiếng Việt', english: 'Vietnamese' },
  { code: 'ja', name: '日本語', english: 'Japanese' },
  { code: 'zh-TW', name: '繁體中文', english: 'Chinese (Traditional)' },
]

export const DEFAULT_LANGUAGE: LanguageCode = 'en'
export const LANG_STORAGE_KEY = 'mgs-lang'

type Dict = Record<string, string>
type Vars = Record<string, string | number | null | undefined>

// Only files that exist are bundled; each becomes its own lazy chunk. Files starting with "_" are the
// extractor's outputs (scripts/i18n-extract.mjs), not languages.
const loaders = import.meta.glob<{ default: Dict }>(['../locales/*.json', '!../locales/_*.json'])

function loaderFor(lang: LanguageCode): (() => Promise<{ default: Dict }>) | undefined {
  return loaders[`../locales/${lang}.json`]
}

/**
 * Languages people can actually pick: English plus every language whose dictionary exists in
 * src/locales. Offering a language without a dictionary would set <html lang> and date formats to it
 * while every string stays English (WCAG 3.1.1), so the others appear once their file ships.
 */
export const LANGUAGES: ReadonlyArray<LanguageInfo> = SUPPORTED_LANGUAGES.filter(
  (l) => l.code === 'en' || loaderFor(l.code) !== undefined,
)

/** True when there is more than one language to choose from (pickers hide otherwise). */
export const HAS_LANGUAGE_CHOICE = LANGUAGES.length > 1

/** Maps any tag ("pt", "es-MX", "zh-Hant-HK", "EN_us") to an available language, or null. */
export function matchLanguage(tag: string | null | undefined): LanguageCode | null {
  const code = matchSupportedLanguage(tag)
  return code && LANGUAGES.some((l) => l.code === code) ? code : null
}

/** Like matchLanguage but against all 11 product languages, dictionary or not. */
export function matchSupportedLanguage(tag: string | null | undefined): LanguageCode | null {
  if (!tag) return null
  const norm = tag.replace('_', '-').trim()
  const lower = norm.toLowerCase()
  const exact = SUPPORTED_LANGUAGES.find((l) => l.code.toLowerCase() === lower)
  if (exact) return exact.code
  const base = lower.split('-')[0]
  if (base === 'zh') return /hant|tw|hk|mo/.test(lower) ? 'zh-TW' : null
  if (base === 'pt') return 'pt-BR'
  const byBase = SUPPORTED_LANGUAGES.find((l) => l.code.toLowerCase() === base)
  return byBase ? byBase.code : null
}

function readStoredLanguage(): LanguageCode | null {
  try {
    return matchLanguage(localStorage.getItem(LANG_STORAGE_KEY))
  } catch {
    return null
  }
}

function browserLanguage(): LanguageCode | null {
  if (typeof navigator === 'undefined') return null
  const list = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const tag of list) {
    const m = matchLanguage(tag)
    if (m) return m
  }
  return null
}

/** Language before /api/auth/me answers: localStorage "mgs-lang", else the browser, else English. */
export function detectLanguage(): LanguageCode {
  return readStoredLanguage() ?? browserLanguage() ?? DEFAULT_LANGUAGE
}

type I18nState = { lang: LanguageCode; dict: Dict; loading: boolean }

export const useI18nStore = create<I18nState>(() => ({ lang: DEFAULT_LANGUAGE, dict: {}, loading: false }))

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template
  const lang = useI18nStore.getState().lang
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = vars[name]
    if (v === undefined || v === null) return whole
    if (typeof v === 'number') {
      try {
        return new Intl.NumberFormat(lang).format(v)
      } catch {
        return String(v)
      }
    }
    return v
  })
}

/** Translates English text (the key) and fills {placeholders}. */
export function t(text: string, vars?: Vars): string {
  const translated = useI18nStore.getState().dict[text]
  return interpolate(translated || text, vars)
}

/** Picks the singular or plural English template by the active language rules, then translates it. */
export function plural(count: number, one: string, other: string, vars?: Vars): string {
  const lang = useI18nStore.getState().lang
  let category: Intl.LDMLPluralRule = count === 1 ? 'one' : 'other'
  try {
    category = new Intl.PluralRules(lang).select(count)
  } catch {
    /* keep the English rule */
  }
  return t(category === 'one' ? one : other, { count, ...vars })
}

let loadSeq = 0

/**
 * E2E-J8-04: I18nProvider remounts the tree on a language change, so keyboard focus (on the globe, the avatar menu
 * or the settings picker that made the change) dropped to <body>. A control is remembered as a child index path
 * from its closest ancestor with a stable id (#root; Radix adds and removes focus guards around it in <body>), and
 * once the new tree is on screen the same control gets focus again, unless focus has moved elsewhere. A pick made
 * in a popup (a portal outside the page tree) falls back to the last page control that had focus: its trigger.
 */
function focusAnchor(el: Element | null): { baseId: string; path: number[] } | null {
  const path: number[] = []
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    if (node !== el && node.id && !/^radix-|^_r|:/.test(node.id)) return path.length ? { baseId: node.id, path } : null
    if (!node.parentElement) return null
    path.unshift(Array.prototype.indexOf.call(node.parentElement.children, node))
  }
  return null
}

let lastPageFocus: Element | null = null
if (typeof document !== 'undefined') {
  document.addEventListener('focusin', (e) => {
    if (e.target instanceof Element && focusAnchor(e.target)) lastPageFocus = e.target
  }, true)
}

function keepFocusAcrossRemount(): () => void {
  if (typeof document === 'undefined' || typeof requestAnimationFrame === 'undefined') return () => {}
  const before = document.activeElement
  if (!before || before === document.body) return () => {}
  const anchor = focusAnchor(before) ?? (lastPageFocus?.isConnected ? focusAnchor(lastPageFocus) : null)
  if (!anchor) return () => {}
  return () => {
    let tries = 10
    const step = () => {
      const active = document.activeElement
      if (active === before && before.isConnected) {
        if (--tries > 0) requestAnimationFrame(step) // the old tree is still on screen
        return
      }
      if (active && active !== document.body) return // focus moved on purpose
      let target: Element | null | undefined = document.getElementById(anchor.baseId)
      for (const i of anchor.path) target = target?.children[i]
      if (target instanceof HTMLElement) target.focus({ preventScroll: true })
    }
    requestAnimationFrame(step)
  }
}

/**
 * Switches the UI language. Loads the dictionary first (falls back to English when the file does
 * not exist yet), then updates <html lang>, date and number formats, and localStorage.
 */
export async function setLanguage(next: string, opts: { persist?: boolean } = {}): Promise<LanguageCode> {
  const lang = matchLanguage(next) ?? DEFAULT_LANGUAGE
  const seq = ++loadSeq
  let dict: Dict = {}
  const loader = lang === 'en' ? undefined : loaderFor(lang)
  if (loader) {
    useI18nStore.setState({ loading: true })
    try {
      dict = (await loader()).default ?? {}
    } catch {
      dict = {}
    }
  }
  if (seq !== loadSeq) return lang // a newer switch won
  const restoreFocus = lang !== useI18nStore.getState().lang ? keepFocusAcrossRemount() : null
  useI18nStore.setState({ lang, dict, loading: false })
  restoreFocus?.()
  setFormatLocale(lang)
  if (typeof document !== 'undefined') document.documentElement.lang = lang
  if (opts.persist !== false) {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang)
    } catch {
      /* private mode */
    }
  }
  return lang
}

/** Called once before the first render. Resolves quickly even when a dictionary is slow. */
export function initI18n(): Promise<void> {
  setFormatTranslator(t)
  const lang = detectLanguage()
  if (lang === 'en') {
    setFormatLocale('en')
    return Promise.resolve()
  }
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 1500))
  return Promise.race([setLanguage(lang, { persist: false }).then(() => undefined), timeout])
}

/** Applies me.user.language when it differs from the active language (auth.ts calls this). */
export function syncLanguageFromUser(language: string | null | undefined): void {
  const lang = matchLanguage(language)
  if (lang && lang !== useI18nStore.getState().lang) void setLanguage(lang)
}

/** Re-renders on language change. */
export function useI18n() {
  const lang = useI18nStore((s) => s.lang)
  useI18nStore((s) => s.dict)
  return { t, plural, lang, setLanguage, languages: LANGUAGES }
}

/**
 * Remounts the tree when the language changes so every plain t() call re-renders.
 * Router location and the query cache live outside the tree and survive.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const lang = useI18nStore((s) => s.lang)
  useEffect(() => {
    setFormatTranslator(t)
  }, [])
  return createElement(Fragment, { key: lang }, children)
}
